import { Box, Stack, Text, Title } from "@mantine/core";
import type { StepId } from "@pages/Onboarding/steps";
import { useStepContext } from "@pages/Onboarding/context/StepContext";
import "./OnboardingStep.scss";

interface OnboardingStepProps {
  stepId: StepId;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
}

/**
 * OnboardingStep - A simple layout wrapper for onboarding steps
 *
 * Features:
 * - Consistent layout with title and description
 * - Navigation via left sidebar (back button removed by default)
 * - Centered layout for full-screen steps
 *
 * Each step is responsible for its own forward navigation logic.
 * Use the `useOnboardingStore` hook in your step component to:
 * - nextStep() - move to next step
 * - updateStepData() - save step-specific data
 */
export const OnboardingStep = ({
  stepId: _stepId,
  title,
  description,
  children,
  headerAction,
}: OnboardingStepProps) => {
  const { fullScreen } = useStepContext();

  return (
    <Box className={`onboarding-step ${fullScreen ? "full-screen" : ""}`}>
      <Stack gap="sm" className="step-content">
        {/* Header Action (e.g., back button) */}
        {headerAction && (
          <Box className="step-header-action">{headerAction}</Box>
        )}

        {/* Header */}
        <Box className="step-header">
          <Title order={1} className="step-title">
            {title}
          </Title>
          {description && (
            <Box className="step-description">
              {typeof description === "string" ? (
                <Text size="lg" c="dimmed">
                  {description}
                </Text>
              ) : (
                description
              )}
            </Box>
          )}
        </Box>

        {/* Main content - step controls its own navigation */}
        <Box className="step-body">{children}</Box>
      </Stack>
    </Box>
  );
};
